/**
 * 包車方案 /charter-plans（20260611 反饋批次）
 * region × 車款 × 時數 × HKD 價格；建單包車流程的時數選單來源。
 */
import { useState } from "react";
import { Plus, Pencil, Power, PowerOff } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { REGIONS, type Region } from "../../../../shared/const";
import { ActiveBadge, REGION_LOC, THEAD_ROW } from "./base-data-shared";

type Row = {
  id: number;
  region: Region;
  vehicleModelId: number;
  durationHours: number;
  priceHkd: string;
  isActive: "yes" | "no";
};

type ModelRow = { id: number; name: string; isActive: "yes" | "no" };

export default function CharterPlans() {
  const { t } = useLanguage();
  const listQ = trpc.charterPlan.list.useQuery();
  const modelsQ = trpc.vehicleModel.list.useQuery();
  const rows = (listQ.data ?? []) as Row[];
  const models = (modelsQ.data ?? []) as ModelRow[];
  const modelName = (id: number) => models.find((m) => m.id === id)?.name ?? `#${id}`;

  const [fRegion, setFRegion] = useState<"all" | Region>("all");
  const setActive = trpc.charterPlan.setActive.useMutation({ onSuccess: () => listQ.refetch() });

  const filtered = rows.filter((r) => fRegion === "all" || r.region === fRegion);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">
            {t({ "zh-TW": "包車方案", "en-US": "Charter Plans", "zh-CN": "包车方案" })}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {t({
              "zh-TW": "建單包車流程的「時數 — 價格」選單來源：region × 車款 × 時數，價格一律 HKD。",
              "en-US": "Charter duration-price options at booking: region × model × hours, priced in HKD.",
              "zh-CN": "建单包车流程的「时数 — 价格」选单来源：region × 车款 × 时数，价格一律 HKD。",
            })}
          </p>
        </div>
        <PlanDialog models={models} onDone={() => listQ.refetch()} />
      </div>

      <div className="flex items-center gap-2">
        <Select className="w-36" value={fRegion} onChange={(e) => setFRegion(e.target.value as "all" | Region)}>
          <option value="all">{t({ "zh-TW": "全部區域", "en-US": "All regions", "zh-CN": "全部区域" })}</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {t(REGION_LOC[r])}
            </option>
          ))}
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading || modelsQ.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
          ) : filtered.length === 0 ? (
            <p className="p-8 text-center text-[13px] text-muted-foreground">
              {t({ "zh-TW": "查無符合條件的包車方案。", "en-US": "No charter plans match the filters.", "zh-CN": "查无符合条件的包车方案。" })}
            </p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className={THEAD_ROW}>
                  <th className="p-3">{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</th>
                  <th className="p-3">{t({ "zh-TW": "車款", "en-US": "Model", "zh-CN": "车款" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "時數", "en-US": "Hours", "zh-CN": "时数" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "價格 (HKD)", "en-US": "Price (HKD)", "zh-CN": "价格 (HKD)" })}</th>
                  <th className="p-3">{t({ "zh-TW": "狀態", "en-US": "Status", "zh-CN": "状态" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Action", "zh-CN": "操作" })}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0">
                    <td className="p-3">{t(REGION_LOC[r.region])}</td>
                    <td className="p-3">{modelName(r.vehicleModelId)}</td>
                    <td className="p-3 text-right numeric">
                      {r.durationHours} {t({ "zh-TW": "小時", "en-US": "hr", "zh-CN": "小时" })}
                    </td>
                    <td className="p-3 text-right numeric font-medium">{Number(r.priceHkd).toLocaleString()}</td>
                    <td className="p-3"><ActiveBadge active={r.isActive} /></td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <PlanDialog plan={r} models={models} onDone={() => listQ.refetch()} />
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={setActive.isPending}
                        onClick={() => setActive.mutate({ id: r.id, isActive: r.isActive === "yes" ? "no" : "yes" })}
                      >
                        {r.isActive === "yes" ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                        {r.isActive === "yes"
                          ? t({ "zh-TW": "停用", "en-US": "Disable", "zh-CN": "停用" })
                          : t({ "zh-TW": "啟用", "en-US": "Enable", "zh-CN": "启用" })}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PlanDialog({ plan, models, onDone }: { plan?: Row; models: ModelRow[]; onDone: () => void }) {
  const { t } = useLanguage();
  // 下拉只列啟用中車款（表格仍可顯示停用車款的既有列）
  const activeModels = models.filter((md) => md.isActive === "yes");
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState<Region>(plan?.region ?? "taiwan");
  const [vehicleModelId, setVehicleModelId] = useState<number>(plan?.vehicleModelId ?? activeModels[0]?.id ?? 0);
  const [durationHours, setDurationHours] = useState(plan?.durationHours ?? 4);
  const [priceHkd, setPriceHkd] = useState(plan ? Number(plan.priceHkd) : 0);

  const m = trpc.charterPlan.upsert.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  const invalid =
    vehicleModelId <= 0 ||
    !Number.isFinite(durationHours) ||
    !Number.isFinite(priceHkd) ||
    durationHours < 1 ||
    durationHours > 48 ||
    priceHkd <= 0;

  // 開啟時從 props/初始值重新同步草稿（避免殘留上一次新增/取消的輸入）；
  // 新增時順便補上預設車款（dialog 可能在 models 載入前掛載，初始 0）
  const changeOpen = (o: boolean) => {
    setOpen(o);
    if (o) {
      setRegion(plan?.region ?? "taiwan");
      setVehicleModelId(plan?.vehicleModelId ?? activeModels[0]?.id ?? 0);
      setDurationHours(plan?.durationHours ?? 4);
      setPriceHkd(plan ? Number(plan.priceHkd) : 0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        {plan ? (
          <Button variant="ghost" size="sm">
            <Pencil className="h-3.5 w-3.5" />
            {t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}
          </Button>
        ) : (
          <Button size="sm">
            <Plus className="h-3.5 w-3.5" />
            {t({ "zh-TW": "新增方案", "en-US": "New plan", "zh-CN": "新增方案" })}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {plan
              ? t({ "zh-TW": "編輯包車方案", "en-US": "Edit charter plan", "zh-CN": "编辑包车方案" })
              : t({ "zh-TW": "新增包車方案", "en-US": "New charter plan", "zh-CN": "新增包车方案" })}
          </DialogTitle>
          <DialogDescription>
            {t({ "zh-TW": "時數 1–48 小時；價格一律 HKD。", "en-US": "1–48 hours; priced in HKD.", "zh-CN": "时数 1–48 小时；价格一律 HKD。" })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</Label>
              <Select value={region} onChange={(e) => setRegion(e.target.value as Region)}>
                {REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {t(REGION_LOC[r])}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "車款", "en-US": "Model", "zh-CN": "车款" })}</Label>
              <Select value={String(vehicleModelId)} onChange={(e) => setVehicleModelId(Number(e.target.value))}>
                {activeModels.map((md) => (
                  <option key={md.id} value={md.id}>
                    {md.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "時數", "en-US": "Hours", "zh-CN": "时数" })}</Label>
              <Input type="number" min={1} max={48} value={durationHours} onChange={(e) => setDurationHours(Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "價格", "en-US": "Price", "zh-CN": "价格" })}（HKD）</Label>
              <Input type="number" min={1} value={priceHkd} onChange={(e) => setPriceHkd(Number(e.target.value))} />
            </div>
          </div>
          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button
            onClick={() => m.mutate({ id: plan?.id, region, vehicleModelId, durationHours, priceHkd })}
            disabled={m.isPending || invalid}
          >
            {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "保存" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
